import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { authApi } from '@/services/api'
import { useAuthStore } from '@/store'
import { isPhoneDevice } from '@/utils/talkWelt'
import { isStandaloneDisplay, webAuthnAvailable } from '@/utils/pwaDisplay'
import { createPasskey, isWebAuthnCancel } from '@/utils/webauthn'
import { isFaceIdUserAgent } from '@/utils/phoneUa'
import {
  clearPasskeyOffer,
  isFaceIdClient,
  isPasskeyEnrolledSync,
  isPasskeyEnrollSnoozed,
  isPasskeyOfferArmed,
  markPasskeyEnrolled,
  shouldOfferPasskeyEnroll,
  snoozePasskeyEnroll,
} from '@/utils/deviceUnlock'

export function WebAuthnEnrollModal() {
  const { t } = useTranslation()
  const pending = useAuthStore((s) => s.pendingWebAuthnEnroll)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const user = useAuthStore((s) => s.user)
  const clear = useAuthStore((s) => s.clearWebAuthnEnroll)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const registerRef = useRef<Record<string, unknown> | null>(null)
  const [registerReady, setRegisterReady] = useState(false)
  const [snoozed, setSnoozed] = useState(isPasskeyEnrollSnoozed)
  const prompted = typeof localStorage !== 'undefined' && localStorage.getItem('sv.webauthn.prompted') === '1'
  const phone = isPhoneDevice() || isFaceIdUserAgent(typeof navigator !== 'undefined' ? navigator.userAgent : '')
  const standalone = isStandaloneDisplay()
  const armed = isPasskeyOfferArmed()

  useEffect(() => {
    setSnoozed(isPasskeyEnrollSnoozed())
  }, [pending, isAuthenticated])

  useEffect(() => {
    if (pending && prompted && !phone && !standalone && !isFaceIdClient()) clear()
  }, [pending, prompted, phone, standalone, clear])

  useEffect(() => {
    if (!isAuthenticated || !webAuthnAvailable()) return
    let cancelled = false
    void authApi.webauthnRegisterOptions().then((options) => {
      if (cancelled) return
      registerRef.current = options
      setRegisterReady(true)
    }).catch(() => {
      if (!cancelled) setRegisterReady(false)
    })
    return () => {
      cancelled = true
    }
  }, [isAuthenticated, pending, armed])

  const schoolPick = typeof window !== 'undefined' && window.location.pathname === '/engineer/schools'
  const needsSchool = Boolean(user?.needs_school_selection) && !user?.school_id
  const offer = shouldOfferPasskeyEnroll({
    pending,
    authenticated: isAuthenticated,
    webauthn: webAuthnAvailable(),
    enrolled: isPasskeyEnrolledSync(),
    prompted,
    phone,
    standalone,
    snoozed,
    blocked: schoolPick || needsSchool,
    armed,
  })
  if (!offer || typeof document === 'undefined') return null

  async function enroll() {
    const cached = registerRef.current
    if (!cached) return
    registerRef.current = null
    setRegisterReady(false)
    const pendingCred = createPasskey(cached)
    setBusy(true)
    setError(null)
    try {
      const credential = await pendingCred
      await authApi.webauthnRegister(credential, window.location.origin)
      markPasskeyEnrolled()
      clearPasskeyOffer()
      localStorage.setItem('sv.webauthn.prompted', '1')
      clear()
    } catch (e) {
      void authApi.webauthnRegisterOptions().then((options) => {
        registerRef.current = options
        setRegisterReady(true)
      }).catch(() => {})
      if (isWebAuthnCancel(e)) {
        setBusy(false)
        return
      }
      setError(t(phone ? 'login_faceid_enroll_error' : 'login_passkey_enroll_error'))
      setBusy(false)
    }
  }

  function dismiss() {
    clearPasskeyOffer()
    if (phone || standalone) {
      snoozePasskeyEnroll()
      setSnoozed(true)
    } else {
      localStorage.setItem('sv.webauthn.prompted', '1')
    }
    clear()
  }

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-2xl bg-slate-900 text-slate-100 p-6 space-y-4">
        <h2 className="text-lg font-semibold">{t(phone ? 'login_faceid_prompt_title' : 'login_passkey_prompt_title')}</h2>
        <p className="text-sm text-slate-300">{t(phone ? 'login_faceid_prompt_text' : 'login_passkey_prompt_text')}</p>
        {error && <p className="text-sm text-red-400">{error}</p>}
        <button
          type="button"
          disabled={busy || !registerReady}
          onClick={() => void enroll()}
          className="w-full py-2 rounded-md bg-blue-600 text-white text-sm font-medium disabled:opacity-50"
        >
          {busy ? t('login_faceid_saving') : t('login_faceid_remember')}
        </button>
        <button type="button" onClick={dismiss} className="w-full py-2 text-sm text-slate-400">
          {t('login_faceid_later')}
        </button>
      </div>
    </div>,
    document.body,
  )
}
